/**
 * SettingsPage — 设置页（docs/ui_userinfo 规范）
 * 面板化布局：白底 + 阴影 + 占满 100% 宽度（Card 默认 appearance）
 * 页面内不渲染 H1（标题由顶栏面包屑提供）
 * 退出登录：二次确认 Dialog（DialogCloseTitle 右上角 ✕，底部仅动作按钮）
 */
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  makeStyles,
  tokens,
  Text,
  Switch,
  Card,
  CardHeader,
  Button,
  Badge,
  MessageBar,
  MessageBarBody,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogContent,
  DialogActions,
  Input,
  Field,
  InfoLabel,
  Combobox,
  Option,
  Textarea,
  Select,
  Divider,
} from '@fluentui/react-components'
import {
  BeakerRegular,
  PersonRegular,
  MailRegular,
  ShieldRegular,
  SignOutRegular,
  SparkleRegular,
  AddRegular,
  EditRegular,
  DeleteRegular,
  SaveRegular,
  ArchiveRegular,
} from '@fluentui/react-icons'
import { useSettingsStore } from '@/store/settings'
import { useAuthStore } from '@/store/auth'
import { ROUTES } from '@/routes'
import { DialogCloseTitle } from '@/components/DialogCloseTitle'
import { toast } from '@/lib/toast'
import { ApiError } from '@/api/client'
import { llmApi } from '@/api/llm'
import { retentionApi, type RetentionPolicyInput } from '@/api/retention'
import { queueConfigApi, type QueueConfigInput } from '@/api/queueConfig'
import { oauthApi, type OAuthProviderConfig } from '@/api/oauth'
import type { LlmProvider, LlmProviderType, RetentionPolicy, QueueConfig } from '@/api/types'

const useStyles = makeStyles({
  // 页面根容器：与列表页一致的左右留白；不设 maxWidth，面板占满 100% 宽
  root: {
    padding: '24px 32px',
    width: '100%',
  },
  // 面板：白底 + 阴影（Card 默认 appearance）；占满 100% 所在宽度
  panel: {
    width: '100%',
    padding: '16px',
    marginBottom: '16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  // 面板标题行：标题居左，操作按钮居右
  panelHeaderRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  cardHeader: {
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase400,
    color: tokens.colorNeutralForeground1,
  },
  hint: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    marginTop: '0',
    marginBottom: '0',
  },
  // ── 描述网格（只读字段：标签列 + 值列） ──
  descGrid: {
    display: 'flex',
    flexDirection: 'column',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    overflow: 'hidden',
  },
  descRow: {
    display: 'flex',
    alignItems: 'stretch',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    ':last-child': { borderBottom: 'none' },
  },
  descLabel: {
    width: '140px',
    flexShrink: 0,
    padding: '10px 16px',
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    backgroundColor: tokens.colorNeutralBackground2,
    display: 'flex',
    alignItems: 'center',
  },
  descValue: {
    flex: 1,
    minWidth: 0,
    padding: '10px 16px',
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground1,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  // 退出登录按钮（危险色用 inline style，Griffel 不接受 status token 于 borderColor）
  logoutBtn: {
    alignSelf: 'flex-start',
  },
  dialogSurfaceSmall: {
    width: '480px',
    maxWidth: '90vw',
  },
  dialogSurfaceMid: {
    width: '560px',
    maxWidth: '92vw',
  },
  // ── AI 服务商面板 ──
  providerRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '10px 12px',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    ':hover': { backgroundColor: tokens.colorNeutralBackground2 },
  },
  providerInfo: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  providerNameRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  providerMeta: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  providerActions: {
    display: 'flex',
    gap: '4px',
    flexShrink: 0,
  },
  formGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '12px 16px',
  },
  formGridItem: { minWidth: 0 },
  colSpan2: { gridColumn: '1 / -1' },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '8px',
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    paddingTop: '16px',
    marginTop: '4px',
  },
  modelChips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px',
    alignItems: 'center',
  },
})

export default function SettingsPage() {
  const styles = useStyles()
  const navigate = useNavigate()
  const user = useAuthStore((s) => s.user)
  const logout = useAuthStore((s) => s.logout)
  const { debugMode, setDebugMode } = useSettingsStore()

  const [logoutOpen, setLogoutOpen] = useState(false)

  const doLogout = () => {
    setLogoutOpen(false)
    logout()
    navigate(ROUTES.LOGIN, { replace: true })
  }

  const roleLabel = user?.is_admin ? '管理员' : user?.role || '用户'

  // ── AI 服务商（LLM）管理 ──
  const queryClient = useQueryClient()
  const { data: providers = [] } = useQuery({
    queryKey: ['llm-providers'],
    queryFn: llmApi.list,
  })

  const [editorOpen, setEditorOpen] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [form, setForm] = useState({
    name: '',
    provider_type: 'openai' as LlmProviderType,
    base_url: '',
    api_key: '',
    models: [] as string[],
    default_model: '',
    enabled: true,
  })
  const [modelText, setModelText] = useState('')
  const [deleteTarget, setDeleteTarget] = useState<LlmProvider | null>(null)

  const PROVIDER_TYPE_LABELS: Record<LlmProviderType, string> = {
    openai: 'OpenAI 兼容',
    ollama: 'Ollama',
    lmstudio: 'LM Studio',
  }

  // 各类型默认值（选择类型时自动填充）
  const TYPE_PRESETS: Record<LlmProviderType, { name: string; base_url: string; api_key: string }> = {
    openai: { name: '', base_url: 'https://api.openai.com/v1', api_key: '' },
    ollama: { name: '本地 Ollama', base_url: 'http://localhost:11434/v1', api_key: '' },
    lmstudio: { name: 'LM Studio', base_url: 'http://localhost:1234/v1', api_key: '' },
  }

  const onTypeChange = (vt: LlmProviderType) => {
    setForm((f) => {
      const preset = TYPE_PRESETS[vt]
      const prev = TYPE_PRESETS[f.provider_type]
      // 仅当字段为空或等于上一类型的预设值时，才替换为新类型预设（避免覆盖用户手动输入）
      const isPreset = (val: string, prevVal: string) => !val || val === prevVal
      return {
        ...f,
        provider_type: vt,
        name: isPreset(f.name, prev.name) ? preset.name : f.name,
        base_url: isPreset(f.base_url, prev.base_url) ? preset.base_url : f.base_url,
        api_key: isPreset(f.api_key, prev.api_key) ? preset.api_key : f.api_key,
      }
    })
  }

  // 测试连通 —— 成功后自动加载模型列表
  const testConnMutation = useMutation({
    mutationFn: () =>
      llmApi.testConnection({
        provider_type: form.provider_type,
        base_url: form.base_url.trim(),
        api_key: form.api_key.trim(),
      }),
    onSuccess: (res) => {
      const models = res.models ?? []
      setModelText(models.join('\n'))
      setForm((f) => ({ ...f, default_model: f.default_model || (models[0] ?? '') }))
      toast.success('连通成功', `共加载 ${models.length} 个模型，已填入模型列表` + (res.resolvedUrl ? `（${res.resolvedUrl}）` : ''))
    },
    onError: (err) => toast.error('连通失败', err instanceof ApiError ? err.message : '无法连接服务商'),
  })

  const openCreate = () => {
    setEditingId(null)
    setForm({ name: '', provider_type: 'openai', base_url: '', api_key: '', models: [], default_model: '', enabled: true })
    setModelText('')
    setEditorOpen(true)
  }

  const openEdit = (p: LlmProvider) => {
    setEditingId(p.id)
    setForm({
      name: p.name,
      provider_type: p.provider_type,
      base_url: p.base_url,
      api_key: p.api_key,
      models: p.models ?? [],
      default_model: p.default_model,
      enabled: p.enabled,
    })
    setModelText((p.models ?? []).join('\n'))
    setEditorOpen(true)
  }

  const saveProviderMutation = useMutation({
    mutationFn: () => {
      const models = modelText.split('\n').map((m) => m.trim()).filter(Boolean)
      const payload = {
        name: form.name.trim(),
        provider_type: form.provider_type,
        base_url: form.base_url.trim(),
        api_key: form.api_key.trim(),
        models,
        default_model: form.default_model,
        enabled: form.enabled,
      }
      if (editingId === null) return llmApi.create(payload)
      return llmApi.update(editingId, payload)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['llm-providers'] })
      toast.success(editingId === null ? '服务商已添加' : '服务商已更新')
      setEditorOpen(false)
    },
    onError: (err) => toast.error('保存失败', err instanceof ApiError ? err.message : '请稍后重试'),
  })

  const deleteProviderMutation = useMutation({
    mutationFn: async () => {
      if (!deleteTarget) return
      await llmApi.remove(deleteTarget.id)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['llm-providers'] })
      toast.success('已删除', `${deleteTarget?.name} 已删除`)
      setDeleteTarget(null)
    },
    onError: (err) => toast.error('删除失败', err instanceof ApiError ? err.message : '请稍后重试'),
  })

  const toggleEnabledMutation = useMutation({
    mutationFn: ({ id, enabled }: { id: number; enabled: boolean }) =>
      llmApi.update(id, { enabled }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['llm-providers'] })
    },
    onError: (err) => toast.error('更新失败', err instanceof ApiError ? err.message : '请稍后重试'),
  })

  // ── 队列清理配置 ──
  const { data: queuePolicy } = useQuery({
    queryKey: ['queue-config'],
    queryFn: queueConfigApi.get,
  })
  const [queueForm, setQueueForm] = useState<QueueConfig | null>(null)
  const activeQueue = queueForm ?? queuePolicy
  const queueDirty = queueForm !== null

  const saveQueueMutation = useMutation({
    mutationFn: async (input: QueueConfigInput) => queueConfigApi.put(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['queue-config'] })
      toast.success('已保存', '队列清理配置已生效')
      setQueueForm(null)
    },
    onError: (err) => toast.error('保存失败', err instanceof ApiError ? err.message : '请检查输入'),
  })

  const handleQueueSave = () => {
    if (!activeQueue) return
    const days = Number(activeQueue.retention_days)
    const hours = Number(activeQueue.cleanup_interval_hours)
    if (!days || days < 1) return toast.error('请填写有效的保留天数（≥1）')
    if (!hours || hours < 1) return toast.error('请填写有效的清理间隔（≥1 小时）')
    saveQueueMutation.mutate({ retention_days: days, cleanup_interval_hours: hours })
  }

  // ── 保留与过期归档 ──
  const { data: retentionPolicy } = useQuery({
    queryKey: ['retention'],
    queryFn: retentionApi.get,
  })
  const [retentionForm, setRetentionForm] = useState<RetentionPolicy | null>(null)
  const activeRetention = retentionForm ?? retentionPolicy
  const retentionDirty = retentionForm !== null

  const saveRetentionMutation = useMutation({
    mutationFn: async (input: RetentionPolicyInput) => retentionApi.put(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['retention'] })
      toast.success('已保存', '保留策略已生效')
      setRetentionForm(null)
    },
    onError: (err) => toast.error('保存失败', err instanceof ApiError ? err.message : '请检查输入'),
  })

  const handleRetentionSave = () => {
    if (!activeRetention) return
    const days = Number(activeRetention.retention_days)
    if (!days || days < 1) return toast.error('请填写有效的保留天数（≥1）')
    saveRetentionMutation.mutate({
      retention_days: days,
      archive_mode: activeRetention.archive_mode,
      archive_dir: activeRetention.archive_dir.trim() || '/var/lib/mailman/archive',
      enabled: activeRetention.enabled,
    })
  }

  // ── OAuth2 / SSO 配置 ──
  const { data: oauthCfg } = useQuery({
    queryKey: ['oauth-config'],
    queryFn: oauthApi.getConfig,
  })
  const [oauthLocalLogin, setOauthLocalLogin] = useState<boolean | null>(null)
  const [oauthEnabled, setOauthEnabled] = useState<boolean | null>(null)
  const [oauthProviders, setOauthProviders] = useState<OAuthProviderConfig[] | null>(null)
  const activeLocalLogin = oauthLocalLogin ?? oauthCfg?.local_login_enabled ?? true
  const activeOauthEnabled = oauthEnabled ?? oauthCfg?.oauth_enabled ?? true
  const activeProviders = oauthProviders ?? oauthCfg?.providers ?? []
  const oauthDirty =
    oauthLocalLogin !== null || oauthEnabled !== null || oauthProviders !== null

  const emptyOauthProvider = (): OAuthProviderConfig => ({
    name: '',
    display_name: '',
    enabled: true,
    flow: 'authorization_code',
    base_url: '',
    client_id: '',
    client_secret: '',
    scopes: 'openid profile email',
    admin_role: 'admin',
    redirect_uri: null,
    auto_create: true,
    authorization_endpoint: '',
    token_endpoint: '',
    userinfo_endpoint: '',
  })

  const saveOauthMutation = useMutation({
    mutationFn: () =>
      oauthApi.updateConfig({
        local_login_enabled: activeLocalLogin,
        oauth_enabled: activeOauthEnabled,
        providers: activeProviders,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['oauth-config'] })
      setOauthLocalLogin(null)
      setOauthEnabled(null)
      setOauthProviders(null)
      toast.success('已保存', 'OAuth2 配置已生效')
    },
    onError: (err) => toast.error('保存失败', err instanceof ApiError ? err.message : '请稍后重试'),
  })

  // provider 编辑
  const [oauthEditorOpen, setOauthEditorOpen] = useState(false)
  const [oauthEditing, setOauthEditing] = useState<number | null>(null)
  const [oauthForm, setOauthForm] = useState<OAuthProviderConfig>(emptyOauthProvider())

  const openOauthCreate = () => {
    setOauthEditing(null)
    setOauthForm(emptyOauthProvider())
    setOauthEditorOpen(true)
  }
  const openOauthEdit = (idx: number) => {
    setOauthEditing(idx)
    setOauthForm({ ...activeProviders[idx] })
    setOauthEditorOpen(true)
  }
  const saveOauthProvider = () => {
    const p = { ...oauthForm, name: oauthForm.name.trim() }
    const list = [...activeProviders]
    if (oauthEditing === null) list.push(p)
    else list[oauthEditing] = p
    setOauthProviders(list)
    setOauthEditorOpen(false)
  }
  const removeOauthProvider = (idx: number) => {
    setOauthProviders(activeProviders.filter((_, i) => i !== idx))
  }

  return (
    <div className={styles.root}>
      {/* ① 基本信息（只读） */}
      <Card className={styles.panel}>
        <CardHeader header={<Text className={styles.cardHeader}>基本信息</Text>} />
        <div className={styles.descGrid}>
          <div className={styles.descRow}>
            <div className={styles.descLabel}>用户名</div>
            <div className={styles.descValue}>
              <PersonRegular />
              {user?.username ?? '—'}
            </div>
          </div>
          <div className={styles.descRow}>
            <div className={styles.descLabel}>邮箱</div>
            <div className={styles.descValue}>
              <MailRegular />
              {user?.email || '—'}
            </div>
          </div>
          <div className={styles.descRow}>
            <div className={styles.descLabel}>角色</div>
            <div className={styles.descValue}>
              <ShieldRegular />
              <Badge appearance="filled" color={user?.is_admin ? 'danger' : 'brand'}>
                {roleLabel}
              </Badge>
            </div>
          </div>
        </div>
      </Card>

      {/* ② AI 服务商（LLM） — 邮件模板 AI 生成配置 */}
      <Card className={styles.panel}>
        <div className={styles.panelHeaderRow}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <SparkleRegular />
            <Text className={styles.cardHeader}>AI 服务商（LLM）</Text>
          </div>
          <Button appearance="primary" icon={<AddRegular />} onClick={openCreate}>
            添加服务商
          </Button>
        </div>
        <Text className={styles.hint}>
          配置用于「邮件模板」AI 生成的服务商，支持 OpenAI 兼容 API、Ollama、LM Studio。配置后即可在新建/编辑模板时使用「AI 生成」。
        </Text>
        {providers.length === 0 ? (
          <Text className={styles.hint}>尚未配置任何 AI 服务商。</Text>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {providers.map((p) => (
              <div key={p.id} className={styles.providerRow}>
                <div className={styles.providerInfo}>
                  <div className={styles.providerNameRow}>
                    <Text weight="semibold">{p.name}</Text>
                    <Badge appearance="filled" color={p.provider_type === 'ollama' ? 'brand' : p.provider_type === 'lmstudio' ? 'severe' : 'informative'}>
                      {PROVIDER_TYPE_LABELS[p.provider_type] ?? p.provider_type}
                    </Badge>
                    {!p.enabled && <Badge appearance="outline" color="subtle">已停用</Badge>}
                  </div>
                  <div className={styles.providerMeta}>
                    {p.base_url || '（未填地址）'} · 默认模型：{p.default_model || '未设置'}
                  </div>
                </div>
                <div className={styles.providerActions}>
                  <Switch
                    checked={p.enabled}
                    onChange={(_, d) => toggleEnabledMutation.mutate({ id: p.id, enabled: d.checked })}
                    label={p.enabled ? '启用' : '停用'}
                  />
                  <Button appearance="subtle" size="small" icon={<EditRegular />} aria-label="编辑" onClick={() => openEdit(p)} />
                  <Button appearance="subtle" size="small" icon={<DeleteRegular />} aria-label="删除" onClick={() => setDeleteTarget(p)} />
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* ③ 调试模式（系统设置） */}
      <Card className={styles.panel}>
        <div className={styles.panelHeaderRow}>
          <Text className={styles.cardHeader}>调试模式</Text>
          <Switch checked={debugMode} onChange={(_, d) => setDebugMode(d.checked)} />
        </div>
        <Text className={styles.hint}>
          开启后将显示「邮件测试」菜单，提供收发邮件等测试功能。
        </Text>
        {debugMode && (
          <MessageBar intent="info">
            <MessageBarBody>
              <BeakerRegular fontSize={16} /> 调试模式已开启：左侧导航已显示「邮件测试」菜单。测试功能仅用于开发调试，请勿在生产环境长期开启。
            </MessageBarBody>
          </MessageBar>
        )}
      </Card>

      {/* ④ 队列清理配置 */}
      <Card className={styles.panel}>
        <div className={styles.panelHeaderRow}>
          <Text className={styles.cardHeader}>队列清理配置</Text>
        </div>
        <Text className={styles.hint}>
          控制投递队列中终态条目（已送达 / 已退回 / 已过期）的保留策略。调度器会按设置定期清理旧条目及其关联数据，避免磁盘空间持续占用。
        </Text>
        <div className={styles.formGrid}>
          <Field
            className={styles.formGridItem}
            label={<InfoLabel info="终态邮件保留天数，超过此天数的条目将被清理。">保留天数</InfoLabel>}
            required
          >
            <Input
              type="number"
              value={String(activeQueue?.retention_days ?? 30)}
              onChange={(_, d) =>
                setQueueForm((f) => ({ ...(f ?? activeQueue!), retention_days: Number(d.value) }))
              }
              min={1}
              max={3650}
              style={{ maxWidth: '200px' }}
            />
          </Field>
          <Field
            className={styles.formGridItem}
            label={<InfoLabel info="自动清理执行间隔（小时）。">清理间隔</InfoLabel>}
            required
          >
            <Input
              type="number"
              value={String(activeQueue?.cleanup_interval_hours ?? 1)}
              onChange={(_, d) =>
                setQueueForm((f) => ({ ...(f ?? activeQueue!), cleanup_interval_hours: Number(d.value) }))
              }
              min={1}
              max={24}
              style={{ maxWidth: '200px' }}
            />
          </Field>
        </div>
        <div className={styles.hint}>
          当前策略更新时间：{activeQueue?.updated_at ?? '尚未保存过'}
        </div>
        <div className={styles.actions}>
          {queueDirty && (
            <Button appearance="subtle" onClick={() => setQueueForm(null)}>
              撤销
            </Button>
          )}
          <Button
            appearance="primary"
            icon={<SaveRegular />}
            onClick={handleQueueSave}
            disabled={saveQueueMutation.isPending}
          >
            {saveQueueMutation.isPending ? '保存中…' : '保存队列配置'}
          </Button>
        </div>
      </Card>

      {/* ⑤ 保留与过期归档 */}
      <Card className={styles.panel}>
        <div className={styles.panelHeaderRow}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <ArchiveRegular />
            <Text className={styles.cardHeader}>保留与过期归档</Text>
          </div>
        </div>
        <Text className={styles.hint}>
          配置默认保留期与过期邮件的归档方式。归档为每小时后台增量任务，仅处理超过保留期的旧邮件，不影响新邮件写入。
        </Text>
        <div className={styles.formGrid}>
          <Field
            className={styles.formGridItem}
            label={<InfoLabel info="超过该天数且未归档的邮件将被处理。">保留天数</InfoLabel>}
            required
          >
            <Input
              type="number"
              value={String(activeRetention?.retention_days ?? 180)}
              onChange={(_, d) =>
                setRetentionForm((f) => ({ ...(f ?? activeRetention!), retention_days: Number(d.value) }))
              }
              min={1}
              max={3650}
              style={{ maxWidth: '200px' }}
            />
          </Field>
          <Field
            className={styles.formGridItem}
            label={<InfoLabel info="archive：写入归档目录并标记；delete：直接删除原文。">归档模式</InfoLabel>}
          >
            <Select
              value={activeRetention?.archive_mode ?? 'archive'}
              onChange={(_, d) =>
                setRetentionForm((f) => ({ ...(f ?? activeRetention!), archive_mode: d.value }))
              }
              style={{ maxWidth: '240px' }}
            >
              <option value="archive">归档（archive）</option>
              <option value="delete">直接清理（delete）</option>
            </Select>
          </Field>
          <Field
            className={`${styles.formGridItem} ${styles.colSpan2}`}
            label={<InfoLabel info="archive 模式下原始邮件写入的目录。">归档目录</InfoLabel>}
            hint="仅 archive 模式使用"
          >
            <Input
              value={activeRetention?.archive_dir ?? '/var/lib/mailman/archive'}
              onChange={(_, d) =>
                setRetentionForm((f) => ({ ...(f ?? activeRetention!), archive_dir: d.value }))
              }
              placeholder="/var/lib/mailman/archive"
            />
          </Field>
          <Field
            className={styles.formGridItem}
            label={<InfoLabel info="关闭后定时任务将跳过归档，已配置的策略保留不动。">启用归档任务</InfoLabel>}
          >
            <Switch
              checked={activeRetention?.enabled ?? false}
              onChange={(_, d) =>
                setRetentionForm((f) => ({ ...(f ?? activeRetention!), enabled: d.checked }))
              }
              label={activeRetention?.enabled ? '已启用' : '已停用'}
            />
          </Field>
        </div>
        <div className={styles.hint}>
          当前策略更新时间：{activeRetention?.updated_at ?? '尚未保存过'}
        </div>
        <div className={styles.actions}>
          {retentionDirty && (
            <Button appearance="subtle" onClick={() => setRetentionForm(null)}>
              撤销
            </Button>
          )}
          <Button
            appearance="primary"
            icon={<SaveRegular />}
            onClick={handleRetentionSave}
            disabled={saveRetentionMutation.isPending}
          >
            {saveRetentionMutation.isPending ? '保存中…' : '保存策略'}
          </Button>
        </div>
      </Card>

      {/* ⑥ OAuth2 / SSO 配置 */}
      <Card className={styles.panel}>
        <div className={styles.panelHeaderRow}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <ShieldRegular />
            <Text className={styles.cardHeader}>OAuth2 / SSO 配置</Text>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <Field label="允许 OAuth2 / SSO">
              <Switch
                checked={activeOauthEnabled}
                onChange={(_, d) => setOauthEnabled(d.checked)}
                label={activeOauthEnabled ? '已开启' : '已关闭'}
              />
            </Field>
            <Field label="允许本地登录">
              <Switch
                checked={activeLocalLogin}
                onChange={(_, d) => setOauthLocalLogin(d.checked)}
                label={activeLocalLogin ? '已开启' : '已关闭'}
              />
            </Field>
            <Button appearance="primary" icon={<AddRegular />} onClick={openOauthCreate}>
              添加提供商
            </Button>
          </div>
        </div>
        <Text className={styles.hint}>
          配置第三方 SSO 提供商。关闭「允许 OAuth2 / SSO」后，登录页将不显示 OAuth 入口按钮，后端也会拒绝 OAuth 流程；关闭「允许本地登录」后，登录页将只显示 SSO 按钮，隐藏本地账号登录表单并在后端拒绝本地登录。
        </Text>
        {activeProviders.length === 0 ? (
          <Text className={styles.hint}>尚未配置任何 OAuth2 提供商。</Text>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {activeProviders.map((p, idx) => (
              <div key={p.name} className={styles.providerRow}>
                <div className={styles.providerInfo}>
                  <div className={styles.providerNameRow}>
                    <Text weight="semibold">{p.name}</Text>
                    <Badge appearance="filled" color={p.flow === 'proxy' ? 'severe' : 'informative'}>
                      {p.flow === 'proxy' ? '代理登录' : '授权码'}
                    </Badge>
                    {!p.enabled && <Badge appearance="outline" color="subtle">已停用</Badge>}
                  </div>
                  <div className={styles.providerMeta}>
                    {p.display_name || p.name} · {p.base_url || '未填基址'}
                  </div>
                </div>
                <div className={styles.providerActions}>
                  <Button appearance="subtle" size="small" icon={<EditRegular />} aria-label="编辑" onClick={() => openOauthEdit(idx)} />
                  <Button appearance="subtle" size="small" icon={<DeleteRegular />} aria-label="删除" onClick={() => removeOauthProvider(idx)} />
                </div>
              </div>
            ))}
          </div>
        )}
        <div className={styles.actions}>
          {oauthDirty && (
            <Button appearance="subtle" onClick={() => { setOauthLocalLogin(null); setOauthEnabled(null); setOauthProviders(null) }}>
              撤销
            </Button>
          )}
          <Button
            appearance="primary"
            icon={<SaveRegular />}
            onClick={() => saveOauthMutation.mutate()}
            disabled={saveOauthMutation.isPending}
          >
            {saveOauthMutation.isPending ? '保存中…' : '保存 OAuth2 配置'}
          </Button>
        </div>
      </Card>

      {/* ⑦ 退出登录 */}
      <Card className={styles.panel}>
        <CardHeader header={<Text className={styles.cardHeader}>退出登录</Text>} />
        <Button
          className={styles.logoutBtn}
          icon={<SignOutRegular />}
          style={{
            backgroundColor: tokens.colorStatusDangerBackground1,
            color: tokens.colorStatusDangerForeground1,
            border: `1px solid ${tokens.colorStatusDangerBorder1}`,
          }}
          onClick={() => setLogoutOpen(true)}
        >
          退出登录
        </Button>
      </Card>

      {/* 退出登录确认 Dialog（右上角 ✕ 关闭，底部仅「退出」动作按钮，docs/ui_dialog） */}
      <Dialog
        open={logoutOpen}
        onOpenChange={(_, d) => {
          if (!d.open) setLogoutOpen(false)
        }}
        modalType="alert"
      >
        <DialogSurface className={styles.dialogSurfaceSmall}>
          <DialogBody>
            <DialogCloseTitle title="确认退出登录？" onClose={() => setLogoutOpen(false)} />
            <DialogContent>退出后需要重新输入用户名 / 密码登录。</DialogContent>
            <DialogActions>
              <Button
                appearance="primary"
                style={{ background: tokens.colorStatusDangerBackground1, color: tokens.colorStatusDangerForeground1 }}
                onClick={doLogout}
              >
                退出
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* AI 服务商编辑 Dialog */}
      <Dialog open={editorOpen} onOpenChange={(_, d) => { if (!d.open) setEditorOpen(false) }} modalType="alert">
        <DialogSurface className={styles.dialogSurfaceMid}>
          <DialogBody>
            <DialogCloseTitle title={editingId === null ? '添加 AI 服务商' : '编辑 AI 服务商'} onClose={() => setEditorOpen(false)} />
            <DialogContent>
              <div className={styles.formGrid}>
                <Field
                  className={styles.formGridItem}
                  label={<InfoLabel info="服务商名称，用于识别。">名称</InfoLabel>}
                  required
                >
                  <Input value={form.name} onChange={(_, d) => setForm((f) => ({ ...f, name: d.value }))} placeholder="例如：DeepSeek、本地 Ollama" />
                </Field>
                <Field
                  className={styles.formGridItem}
                  label={<InfoLabel info="服务商类型：OpenAI 兼容 / Ollama / LM Studio。">类型</InfoLabel>}
                  required
                >
                  <Combobox
                    value={PROVIDER_TYPE_LABELS[form.provider_type]}
                    selectedOptions={[form.provider_type]}
                    onOptionSelect={(_, d) => {
                      const vt = d.optionValue as LlmProviderType
                      if (vt && vt !== form.provider_type) onTypeChange(vt)
                    }}
                  >
                    {(Object.keys(PROVIDER_TYPE_LABELS) as LlmProviderType[]).map((t) => (
                      <Option key={t} value={t}>{PROVIDER_TYPE_LABELS[t]}</Option>
                    ))}
                  </Combobox>
                </Field>
                <Field
                  className={styles.formGridItem}
                  label={<InfoLabel info="API 根地址，不含 /chat/completions。示例：OpenAI 兼容填 https://api.deepseek.com/v1；Ollama 填 http://localhost:11434/v1；LM Studio 填 http://localhost:1234/v1。填入后点击右侧「测试」可校验并自动加载模型列表。">Base URL</InfoLabel>}
                  required
                >
                  <Input
                    value={form.base_url}
                    onChange={(_, d) => setForm((f) => ({ ...f, base_url: d.value }))}
                    placeholder="https://api.example.com/v1"
                    contentAfter={
                      <Button
                        size="small"
                        appearance="subtle"
                        icon={<SparkleRegular />}
                        disabled={testConnMutation.isPending || !form.base_url.trim()}
                        onClick={() => testConnMutation.mutate()}
                      >
                        {testConnMutation.isPending ? '测试中' : '测试'}
                      </Button>
                    }
                  />
                </Field>
                <Field
                  className={styles.formGridItem}
                  label={<InfoLabel info="API Key。Ollama / LM Studio 本地服务通常可留空。">API Key</InfoLabel>}
                >
                  <Input value={form.api_key} onChange={(_, d) => setForm((f) => ({ ...f, api_key: d.value }))} placeholder="可选" />
                </Field>
                <Field
                  className={styles.formGridItem}
                  label={<InfoLabel info="默认模型，AI 生成时若未单独选择则使用该模型。">默认模型</InfoLabel>}
                >
                  <Combobox
                    value={form.default_model}
                    selectedOptions={form.default_model ? [form.default_model] : []}
                    onOptionSelect={(_, d) => setForm((f) => ({ ...f, default_model: d.optionValue ?? '' }))}
                    freeform
                    placeholder="选择或输入模型名"
                  >
                    {modelText.split('\n').map((m) => m.trim()).filter(Boolean).map((m) => (
                      <Option key={m} value={m}>{m}</Option>
                    ))}
                  </Combobox>
                </Field>
                <Field
                  className={`${styles.formGridItem} ${styles.colSpan2}`}
                  label={<InfoLabel info="可用模型列表，每行一个模型名。">模型列表</InfoLabel>}
                >
                  <Textarea
                    rows={3}
                    value={modelText}
                    onChange={(_, d) => setModelText(d.value)}
                    placeholder={'deepseek-chat\ngpt-4o-mini\nqwen2.5'}
                    style={{ fontFamily: 'monospace' }}
                  />
                </Field>
                <div className={`${styles.formGridItem} ${styles.colSpan2}`}>
                  <Divider style={{ marginBottom: '8px' }} />
                  <Switch
                    checked={form.enabled}
                    onChange={(_, d) => setForm((f) => ({ ...f, enabled: d.checked }))}
                    label={form.enabled ? '已启用' : '已停用'}
                  />
                </div>
              </div>
            </DialogContent>
            <DialogActions>
              <Button
                appearance="primary"
                disabled={saveProviderMutation.isPending || !form.name.trim() || !form.base_url.trim()}
                onClick={() => saveProviderMutation.mutate()}
              >
                {saveProviderMutation.isPending ? '保存中…' : '保存'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* 删除 AI 服务商确认 */}
      <Dialog open={!!deleteTarget} onOpenChange={(_, d) => { if (!d.open) setDeleteTarget(null) }} modalType="alert">
        <DialogSurface className={styles.dialogSurfaceSmall}>
          <DialogBody>
            <DialogCloseTitle title="删除 AI 服务商" onClose={() => setDeleteTarget(null)} />
            <DialogContent>
              <Text>确定删除 AI 服务商 <Text weight="semibold">{deleteTarget?.name}</Text> 吗？删除后无法再使用该服务商进行 AI 生成。</Text>
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setDeleteTarget(null)}>取消</Button>
              <Button appearance="primary" onClick={() => deleteProviderMutation.mutate()} disabled={deleteProviderMutation.isPending}>
                {deleteProviderMutation.isPending ? '删除中…' : '确认删除'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* OAuth2 提供商编辑 Dialog */}
      <Dialog open={oauthEditorOpen} onOpenChange={(_, d) => { if (!d.open) setOauthEditorOpen(false) }} modalType="alert">
        <DialogSurface className={styles.dialogSurfaceMid}>
          <DialogBody>
            <DialogCloseTitle title={oauthEditing === null ? '添加 OAuth2 提供商' : '编辑 OAuth2 提供商'} onClose={() => setOauthEditorOpen(false)} />
            <DialogContent>
              <div className={styles.formGrid}>
                <Field
                  className={styles.formGridItem}
                  label={<InfoLabel info="唯一 key，用于 SSO 路由。">名称</InfoLabel>}
                  required
                >
                  <Input value={oauthForm.name} onChange={(_, d) => setOauthForm((f) => ({ ...f, name: d.value }))} placeholder="例如：ming" />
                </Field>
                <Field
                  className={styles.formGridItem}
                  label={<InfoLabel info="登录按钮文案，如「使用 Ming 账号登录」。">显示名称</InfoLabel>}
                >
                  <Input value={oauthForm.display_name} onChange={(_, d) => setOauthForm((f) => ({ ...f, display_name: d.value }))} placeholder="例如：使用 Ming 账号登录" />
                </Field>
                <Field className={styles.formGridItem} label="流程类型">
                  <Select value={oauthForm.flow} onChange={(_, d) => setOauthForm((f) => ({ ...f, flow: d.value }))}>
                    <option value="authorization_code">授权码（authorization_code）</option>
                    <option value="proxy">代理登录（proxy）</option>
                  </Select>
                </Field>
                <Field className={styles.formGridItem} label="启用">
                  <Switch checked={oauthForm.enabled} onChange={(_, d) => setOauthForm((f) => ({ ...f, enabled: d.checked }))} label={oauthForm.enabled ? '已启用' : '已停用'} />
                </Field>
                <Field
                  className={styles.formGridItem}
                  label={<InfoLabel info="IdP 基址，如 http://ming-gateway:8080。">Base URL</InfoLabel>}
                  required
                >
                  <Input value={oauthForm.base_url} onChange={(_, d) => setOauthForm((f) => ({ ...f, base_url: d.value }))} placeholder="http://ming-gateway:8080" />
                </Field>
                <Field className={styles.formGridItem} label="Client ID">
                  <Input value={oauthForm.client_id} onChange={(_, d) => setOauthForm((f) => ({ ...f, client_id: d.value }))} />
                </Field>
                <Field className={styles.formGridItem} label="Client Secret">
                  <Input value={oauthForm.client_secret} onChange={(_, d) => setOauthForm((f) => ({ ...f, client_secret: d.value }))} />
                </Field>
                <Field className={styles.formGridItem} label="Scopes">
                  <Input value={oauthForm.scopes} onChange={(_, d) => setOauthForm((f) => ({ ...f, scopes: d.value }))} />
                </Field>
                <Field
                  className={styles.formGridItem}
                  label={<InfoLabel info="userinfo.role 等于该值时判定为管理员。">Admin 角色</InfoLabel>}
                >
                  <Input value={oauthForm.admin_role} onChange={(_, d) => setOauthForm((f) => ({ ...f, admin_role: d.value }))} />
                </Field>
                <Field className={styles.formGridItem} label="Redirect URI">
                  <Input value={oauthForm.redirect_uri ?? ''} onChange={(_, d) => setOauthForm((f) => ({ ...f, redirect_uri: d.value || null }))} placeholder="可选，默认为本机推导" />
                </Field>
                <Field className={styles.formGridItem} label="授权端点">
                  <Input value={oauthForm.authorization_endpoint} onChange={(_, d) => setOauthForm((f) => ({ ...f, authorization_endpoint: d.value }))} placeholder="可选，缺省从 Base URL 推导" />
                </Field>
                <Field className={styles.formGridItem} label="Token 端点">
                  <Input value={oauthForm.token_endpoint} onChange={(_, d) => setOauthForm((f) => ({ ...f, token_endpoint: d.value }))} placeholder="可选" />
                </Field>
                <Field className={styles.formGridItem} label="UserInfo 端点">
                  <Input value={oauthForm.userinfo_endpoint} onChange={(_, d) => setOauthForm((f) => ({ ...f, userinfo_endpoint: d.value }))} placeholder="可选" />
                </Field>
              </div>
            </DialogContent>
            <DialogActions>
              <Button
                appearance="primary"
                disabled={!oauthForm.name.trim() || !oauthForm.base_url.trim()}
                onClick={saveOauthProvider}
              >
                确定
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  )
}
